import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { allTarotCards } from '../data/tarotCards';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';
import { trackLead } from '../utils/analytics';

// Baralho completo (78 cartas): Arcanos Maiores + Menores. As Menores recebem a
// imagem do Cloudinary via `allTarotCards` (mesmo mapeamento usado na tiragem).
const ALL_CARDS = [...allTarotCards];

// ─── Seleção de 3 cartas por seed da data ────────────────────────────────────
function dateSeed(str: string): number {
  const [y, m, d] = str.split('-').map(Number);
  return (y * 10000 + m * 100 + d) >>> 0;
}

function pick3(seed: number): number[] {
  let s = seed;
  const next = () => { s = (Math.imul(1664525, s) + 1013904223) >>> 0; return s; };
  const indices: number[] = [];
  while (indices.length < 3) {
    const idx = next() % ALL_CARDS.length;
    if (!indices.includes(idx)) indices.push(idx);
  }
  return indices;
}

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatDate(str: string): string {
  const [y, m, d] = str.split('-');
  const months = ['jan','fev','mar','abr','mai','jun','jul','ago','set','out','nov','dez'];
  return `${Number(d)} de ${months[Number(m) - 1]} de ${y}`;
}

function normalizeDate(raw: string): string {
  if (/^\d{8}$/.test(raw)) return `${raw.slice(0,4)}-${raw.slice(4,6)}-${raw.slice(6,8)}`;
  return raw;
}

// ─── Persistência da escolha do dia ──────────────────────────────────────────
// v2: passou a guardar `cardName`. O `cardIdx` sozinho não bastava — quando o
// tamanho do baralho muda (ex.: passou de 22 p/ 78 cartas), o mesmo índice
// sorteado pela seed do dia aponta pra outra carta, e a leitura salva ficava
// descolada da carta mostrada.
const STORAGE_KEY = 'escolha_state_v2';

interface StoredState {
  date: string;
  cardIdx: number;
  cardName: string;
  reading: Reading | null;
}

interface Reading { luz: string; sombra: string; transit_ctx?: string }

// ─── Área da vida — 1 toque, sem digitar nada ────────────────────────────────
// Alimenta o prompt da Aurora pra sair de "genérico igual pra todo mundo" pra
// uma cena concreta ligada ao que a pessoa está vivendo agora. Chave bate com
// `_LIFE_AREAS` em `public_escolha.py` no backend.
const LIFE_AREAS: { key: string; label: string; emoji: string }[] = [
  { key: 'amor',               label: 'Amor',                emoji: '💞' },
  { key: 'trabalho_dinheiro',  label: 'Trabalho & Dinheiro',  emoji: '💼' },
  { key: 'familia',            label: 'Família',              emoji: '🏠' },
  { key: 'decisao',            label: 'Uma decisão',          emoji: '🧭' },
  { key: 'autoconhecimento',   label: 'Autoconhecimento',     emoji: '🌙' },
];

function loadStoredState(today: string, cards: { name: string }[]): StoredState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: StoredState = JSON.parse(raw);
    if (parsed.date !== today) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    // A carta salva precisa bater com a que a seed de hoje realmente mostra.
    if (parsed.cardName && cards[parsed.cardIdx]?.name !== parsed.cardName) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function saveState(state: StoredState) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {}
}

// ─── Componente ───────────────────────────────────────────────────────────────
const EscolhaPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const hasCardBack = !!theme.images.cardBackImageUrl;

  const paramDate = searchParams.get('d');
  const today = todayStr();
  const linkedDate = paramDate ? normalizeDate(paramDate) : null;
  const isExpired = linkedDate !== null && linkedDate !== today;
  const displayDate = linkedDate && !isExpired ? linkedDate : today;

  const [i1, i2, i3] = pick3(dateSeed(displayDate));
  const cards = [ALL_CARDS[i1], ALL_CARDS[i2], ALL_CARDS[i3]];

  // Restaura estado salvo do dia (lazy init)
  const stored = loadStoredState(today, cards);
  const [chosen, setChosen] = useState<number | null>(stored?.cardIdx ?? null);
  const [flipped, setFlipped] = useState(stored !== null);
  const [reading, setReading] = useState<Reading | null>(stored?.reading ?? null);
  const [loadingReading, setLoadingReading] = useState(false);
  const [readingError, setReadingError] = useState(false);
  // Pergunta a área da vida só se ainda não escolheu carta hoje (leitura já salva
  // já carrega a área embutida no texto, não precisa perguntar de novo).
  const [showAreaPicker, setShowAreaPicker] = useState(stored === null);
  const [lifeArea, setLifeArea] = useState<string | null>(null);

  // Se restaurou escolha mas não tem leitura ainda, busca a leitura
  useEffect(() => {
    if (stored !== null && stored.cardIdx !== null && stored.reading === null) {
      const card = cards[stored.cardIdx];
      setLoadingReading(true);
      api.post('/public/card-reading', {
        card_name: card.name,
        card_keywords: card.keywords.join(', '),
        date_str: displayDate,
      }).then(resp => {
        const r = resp.data as Reading;
        setReading(r);
        saveState({ date: today, cardIdx: stored.cardIdx, cardName: card.name, reading: r });
      }).catch(() => {
        setReadingError(true);
      }).finally(() => setLoadingReading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChoose = async (idx: number) => {
    if (chosen !== null) return;
    setChosen(idx);
    // Salva imediatamente — trava a escolha mesmo antes de receber a leitura
    saveState({ date: today, cardIdx: idx, cardName: cards[idx].name, reading: null });
    setTimeout(() => setFlipped(true), 80);

    setLoadingReading(true);
    setReadingError(false);
    const card = cards[idx];
    try {
      const resp = await api.post('/public/card-reading', {
        card_name: card.name,
        card_keywords: card.keywords.join(', '),
        date_str: displayDate,
        life_area: lifeArea || '',
      });
      const r = resp.data as Reading;
      setReading(r);
      saveState({ date: today, cardIdx: idx, cardName: cards[idx].name, reading: r });
      trackLead('escolha');
    } catch {
      setReadingError(true);
    } finally {
      setLoadingReading(false);
    }
  };

  // ─── Tela de leitura expirada ───────────────────────────────────────────────
  if (isExpired) {
    return (
      <div className="min-h-screen bg-gradient-mystical flex flex-col items-center justify-center px-6 text-center">
        <div className="text-6xl mb-6">🌙</div>
        <h1 className="text-3xl font-mystical font-bold text-white mb-3">Esta leitura expirou</h1>
        <p className="text-white/60 text-lg mb-2">
          As cartas de <span className="text-gold-300">{formatDate(linkedDate!)}</span> já não estão mais disponíveis.
        </p>
        <p className="text-white/40 text-base mb-10">Cada leitura é válida apenas no dia em que foi gerada.</p>
        <Link
          to="/escolha"
          className="px-10 py-4 rounded-full font-bold text-white text-base"
          style={{ background: 'linear-gradient(135deg, #7c3aed, #a78bfa)' }}
        >
          Ver as cartas de hoje
        </Link>
      </div>
    );
  }

  // ─── Tela de escolha da área da vida (1 toque, antes das cartas) ─────────────
  if (showAreaPicker) {
    return (
      <div className="min-h-screen bg-gradient-mystical flex flex-col items-center px-6 py-10">
        <Link to="/" className="mb-8 text-white/30 hover:text-white/55 transition-colors text-xs tracking-[0.25em] uppercase font-mystical">
          Astralyz
        </Link>
        <div className="flex-1 flex flex-col items-center justify-center text-center max-w-md w-full">
          <p className="text-white/35 text-xs uppercase tracking-[0.2em] mb-4">{formatDate(today)}</p>
          <h1 className="text-3xl sm:text-4xl font-mystical font-bold text-white mb-3 leading-tight">
            O que está pesando mais em você agora?
          </h1>
          <p className="text-white/50 text-sm sm:text-base leading-relaxed mb-8">
            A carta de hoje vai falar disso, não de generalidades.
          </p>
          <div className="grid grid-cols-1 gap-3 w-full">
            {LIFE_AREAS.map(area => (
              <button
                key={area.key}
                onClick={() => { setLifeArea(area.key); setShowAreaPicker(false); }}
                className="w-full py-4 px-5 rounded-xl text-white text-base font-medium text-left transition-colors hover:bg-white/10 flex items-center gap-3"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)' }}
              >
                <span className="text-xl">{area.emoji}</span>
                {area.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const chosenCard = chosen !== null ? cards[chosen] : null;

  const cardBorderStyle = (active: boolean) => ({
    border: `1px solid ${active ? 'rgba(139,92,246,0.6)' : 'rgba(139,92,246,0.2)'}`,
    boxShadow: active ? '0 0 28px rgba(139,92,246,0.35)' : 'none',
  });

  // ─── Verso da carta ─────────────────────────────────────────────────────────
  const CardBack = ({ active }: { active: boolean }) =>
    hasCardBack ? (
      <div className="w-full h-full rounded-xl overflow-hidden theme-card-back" style={cardBorderStyle(active)} />
    ) : (
      <div
        className="w-full h-full rounded-xl overflow-hidden flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #2d1b69 0%, #1a0a3e 50%, #2d1b69 100%)', ...cardBorderStyle(active) }}
      >
        <span className="text-3xl select-none text-white/40">✦</span>
      </div>
    );

  // ─── Frente da carta ────────────────────────────────────────────────────────
  const CardFront = ({ card }: { card: typeof ALL_CARDS[0] }) =>
    card.image ? (
      <div className="w-full h-full rounded-xl overflow-hidden" style={{ background: '#0d0520' }}>
        <img src={card.image} alt={card.name} className="w-full h-full object-contain" />
      </div>
    ) : (
      <div
        className="w-full h-full rounded-xl overflow-hidden flex flex-col items-center justify-center px-2 text-center"
        style={{ background: 'linear-gradient(135deg, #2d1b69, #7c3aed)' }}
      >
        <span className="text-white font-mystical text-sm font-semibold leading-tight">{card.name}</span>
      </div>
    );

  return (
    <div className="min-h-screen bg-gradient-mystical flex flex-col items-center px-4 py-10">

      {/* Logo */}
      <Link to="/" className="mb-8 text-white/30 hover:text-white/55 transition-colors text-xs tracking-[0.25em] uppercase font-mystical">
        Astralyz
      </Link>

      {/* Título */}
      <div className="text-center mb-6 px-4">
        <p className="text-white/35 text-xs uppercase tracking-[0.2em] mb-4">{formatDate(today)}</p>
        <h1 className="text-4xl sm:text-5xl font-mystical font-bold text-white mb-3 leading-tight">
          {chosen === null ? 'Sua carta do dia' : chosenCard!.name}
        </h1>
        <p className="text-white/50 text-sm sm:text-base leading-relaxed">
          {chosen === null
            ? 'Respire. Deixe uma carta chamar você.'
            : 'Aurora revela o que o dia reserva para você'}
        </p>
      </div>

      {/* Cards */}
      <div className="flex gap-4 sm:gap-8 justify-center mt-10 mb-10">
        {cards.map((card, idx) => {
          const isChosen = chosen === idx;
          const isOther = chosen !== null && !isChosen;

          return (
            <motion.div
              key={idx}
              onClick={() => handleChoose(idx)}
              animate={{
                opacity: isOther ? 0.2 : 1,
                scale: isOther ? 0.88 : 1,
                y: isChosen ? -14 : 0,
              }}
              transition={{ duration: 0.4 }}
              style={{
                width: 'clamp(100px, 26vw, 150px)',
                height: 'clamp(173px, 45vw, 259px)',
                cursor: chosen === null ? 'pointer' : 'default',
                perspective: 1200,
              }}
            >
              <motion.div
                whileHover={chosen === null ? { scale: 1.07, y: -8 } : {}}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                style={{ width: '100%', height: '100%' }}
              >
                <motion.div
                  animate={{ rotateY: isChosen && flipped ? 180 : 0 }}
                  transition={{ duration: 0.65, ease: 'easeInOut' }}
                  style={{ width: '100%', height: '100%', transformStyle: 'preserve-3d', position: 'relative' }}
                >
                  <div className="absolute inset-0" style={{ backfaceVisibility: 'hidden' }}>
                    <CardBack active={chosen === null} />
                  </div>
                  <div className="absolute inset-0" style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}>
                    <CardFront card={card} />
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
          );
        })}
      </div>

      {/* Contexto astrológico */}
      <AnimatePresence>
        {reading?.transit_ctx && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-lg mb-4"
          >
            <div className="rounded-xl px-5 py-3" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <p className="text-white/30 text-xs uppercase tracking-[0.18em] mb-2">Energia do dia</p>
              {reading.transit_ctx.split('\n').filter(l => l.startsWith('•')).map((line, i) => (
                <p key={i} className="text-white/50 text-xs sm:text-sm leading-relaxed">{line}</p>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Leitura */}
      <AnimatePresence>
        {flipped && chosenCard && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.25 }}
            className="w-full max-w-lg space-y-5 mb-12"
          >
            {loadingReading && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="flex flex-col items-center py-12 gap-4">
                <motion.div animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                  className="text-5xl">✦</motion.div>
                <p className="text-white/45 text-base">Aurora está tecendo sua mensagem…</p>
              </motion.div>
            )}

            {readingError && !loadingReading && (
              <div className="rounded-2xl p-6 text-center"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <p className="text-white/50 text-lg">Não foi possível gerar a leitura agora. Tente novamente mais tarde.</p>
              </div>
            )}

            {reading && !loadingReading && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }} className="space-y-5">
                <div className="rounded-2xl p-7"
                  style={{ background: 'linear-gradient(135deg, rgba(251,191,36,0.12) 0%, rgba(251,191,36,0.04) 100%)', border: '1px solid rgba(251,191,36,0.30)' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-lg">☀️</span>
                    <span className="text-xs font-bold text-gold-400 uppercase tracking-[0.2em]">Luz</span>
                  </div>
                  <p className="text-white/90 text-base sm:text-lg leading-loose">{reading.luz}</p>
                </div>

                <div className="rounded-2xl p-7"
                  style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.12) 0%, rgba(99,102,241,0.04) 100%)', border: '1px solid rgba(99,102,241,0.30)' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-lg">🌑</span>
                    <span className="text-xs font-bold text-indigo-400 uppercase tracking-[0.2em]">Sombra</span>
                  </div>
                  <p className="text-white/90 text-base sm:text-lg leading-loose">{reading.sombra}</p>
                </div>

                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="rounded-2xl p-7 text-center"
                  style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.18) 0%, rgba(30,10,60,0.55) 100%)', border: '1px solid rgba(139,92,246,0.35)' }}>
                  {user ? (
                    <>
                      <p className="text-white/40 text-xs uppercase tracking-[0.2em] mb-3">Quer ir mais fundo?</p>
                      <h3 className="text-white font-mystical font-bold text-2xl sm:text-3xl mb-3 leading-snug">
                        Consultas completas com Aurora
                      </h3>
                      <p className="text-white/55 text-sm sm:text-base leading-relaxed mb-6">
                        Tiragens detalhadas de tarot, mapa astral, sinastria, numerologia e muito mais — cada leitura com a profundidade que você merece.
                      </p>
                      <button
                        onClick={() => navigate('/tarot')}
                        className="block w-full py-4 rounded-xl text-white font-bold text-base mb-3 transition-opacity hover:opacity-90"
                        style={{ background: 'linear-gradient(135deg, #7c3aed, #a78bfa)' }}>
                        ✨ Fazer uma consulta completa
                      </button>
                      <button
                        onClick={() => navigate('/checkout')}
                        className="block w-full py-3 rounded-xl text-white/45 text-sm font-medium hover:text-white/70 transition-colors"
                        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                        Ver planos e tokens
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="text-white/40 text-xs uppercase tracking-[0.2em] mb-3">Isso é só o começo</p>
                      <h3 className="text-white font-mystical font-bold text-2xl sm:text-3xl mb-3 leading-snug">
                        Há muito mais guardado para você
                      </h3>
                      <p className="text-white/55 text-sm sm:text-base leading-relaxed mb-6">
                        Leituras completas de tarot, mapa astral, sinastria, numerologia e a Aurora como sua guia espiritual — tudo gratuito para começar.
                      </p>
                      <Link to="/register"
                        className="block w-full py-4 rounded-xl text-white font-bold text-base mb-3 transition-opacity hover:opacity-90"
                        style={{ background: 'linear-gradient(135deg, #7c3aed, #a78bfa)' }}>
                        ✨ Criar conta grátis
                      </Link>
                      <Link to="/login"
                        className="block w-full py-3 rounded-xl text-white/45 text-sm font-medium hover:text-white/70 transition-colors"
                        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                        Já tenho conta
                      </Link>
                    </>
                  )}
                </motion.div>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {chosen === null && (
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}
          className="text-white/18 text-xs tracking-wider mt-2">
          Uma carta nova a cada amanhecer
        </motion.p>
      )}
    </div>
  );
};

export default EscolhaPage;
